import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, MapPin, BookOpen, Truck, User, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { savedAddressAPI } from '../../services/api';
import { toast } from 'sonner';

const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'];

const typeConfig = {
  ship_to: { label: 'Ship To', color: 'bg-blue-100 text-blue-700', icon: Truck },
  consignee: { label: 'Consignee', color: 'bg-amber-100 text-amber-700', icon: User },
};

const emptyForm = {
  address_type: 'ship_to', label: '', name: '', address: '', city: '', state: '', pin: '',
  contact_person: '', contact_phone: '', gstin: '', notes: '',
};

const SavedAddressBook = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const data = filter
        ? await savedAddressAPI.getByType(filter)
        : await savedAddressAPI.getByType('').catch(() => []);
      const allData = filter ? data : await savedAddressAPI.getAll();
      setAddresses(Array.isArray(allData) ? allData : (Array.isArray(data) ? data : []));
    } catch {
      toast.error('Failed to load addresses');
    }
    setLoading(false);
  }, [filter]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      if (filter) {
        const data = await savedAddressAPI.getByType(filter);
        setAddresses(Array.isArray(data) ? data : []);
      } else {
        const [shipTo, consignee] = await Promise.all([
          savedAddressAPI.getByType('ship_to').catch(() => []),
          savedAddressAPI.getByType('consignee').catch(() => []),
        ]);
        setAddresses([...shipTo, ...consignee]);
      }
    } catch {
      toast.error('Failed to load addresses');
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowDialog(true);
  };

  const openEdit = (addr) => {
    setEditingId(addr.id);
    setForm({
      address_type: addr.address_type || 'ship_to',
      label: addr.label || '',
      name: addr.name || '',
      address: addr.address || '',
      city: addr.city || '',
      state: addr.state || '',
      pin: addr.pin || '',
      contact_person: addr.contact_person || '',
      contact_phone: addr.contact_phone || '',
      gstin: addr.gstin || '',
      notes: addr.notes || '',
    });
    setShowDialog(true);
  };

  const openDelete = (addr) => {
    setDeleteTarget(addr);
    setShowDelete(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.address.trim()) { toast.error('Address is required'); return; }
    setSubmitting(true);
    try {
      if (editingId) {
        await savedAddressAPI.update(editingId, form);
        toast.success('Address updated');
      } else {
        await savedAddressAPI.create(form);
        toast.success('Address saved');
      }
      setShowDialog(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save address');
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await savedAddressAPI.delete(deleteTarget.id);
      toast.success('Address deleted');
      setShowDelete(false);
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
    setSubmitting(false);
  };

  const filtered = addresses.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (a.label || '').toLowerCase().includes(q) ||
      (a.name || '').toLowerCase().includes(q) ||
      (a.city || '').toLowerCase().includes(q) ||
      (a.state || '').toLowerCase().includes(q) ||
      (a.gstin || '').toLowerCase().includes(q) ||
      (a.contact_person || '').toLowerCase().includes(q);
  });

  const shipToCount = addresses.filter(a => a.address_type === 'ship_to').length;
  const consigneeCount = addresses.filter(a => a.address_type === 'consignee').length;

  return (
    <div className="space-y-4" data-testid="saved-address-book">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-gray-900">Saved Address Book</h2>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Reusable Ship To & Consignee addresses for Order Confirmations
          </p>
        </div>
        <Button size="sm" onClick={openAdd} data-testid="add-address-btn" className="gap-1.5 bg-[#24549c] hover:bg-[#1a3f7a]">
          <Plus className="w-3.5 h-3.5" /> Add Address
        </Button>
      </div>

      {/* Filter + Search */}
      <div className="flex items-center gap-3">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          {[
            { key: '', label: 'All', count: addresses.length },
            { key: 'ship_to', label: 'Ship To', count: shipToCount },
            { key: 'consignee', label: 'Consignee', count: consigneeCount },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`addr-filter-${f.key || 'all'}`}
              className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-colors ${
                filter === f.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {f.label} ({f.count})
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, city, GSTIN..."
            className="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
            data-testid="addr-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2">
              <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Address List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" data-testid="addr-empty">
          <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">{search ? 'No addresses match your search' : 'No saved addresses yet'}</p>
          <p className="text-[10px] text-gray-300 mt-1">Click "Add Address" to create your first entry</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(addr => {
            const cfg = typeConfig[addr.address_type] || typeConfig.ship_to;
            const TypeIcon = cfg.icon;
            return (
              <div key={addr.id}
                className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all group"
                data-testid={`addr-card-${addr.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      addr.address_type === 'ship_to' ? 'bg-blue-50' : 'bg-amber-50'
                    }`}>
                      <TypeIcon className={`w-3.5 h-3.5 ${
                        addr.address_type === 'ship_to' ? 'text-blue-600' : 'text-amber-600'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-gray-900 truncate">{addr.name}</p>
                      {addr.label && <p className="text-[10px] text-gray-400 truncate">{addr.label}</p>}
                    </div>
                  </div>
                  <Badge className={`text-[9px] flex-shrink-0 ${cfg.color}`}>{cfg.label}</Badge>
                </div>

                <div className="ml-9 space-y-1">
                  <p className="text-[11px] text-gray-600 leading-relaxed">
                    {[addr.address, addr.city, addr.state, addr.pin].filter(Boolean).join(', ')}
                  </p>
                  {addr.gstin && (
                    <p className="text-[10px] text-gray-400 font-mono">GSTIN: {addr.gstin}</p>
                  )}
                  {(addr.contact_person || addr.contact_phone) && (
                    <p className="text-[10px] text-gray-400">
                      {addr.contact_person && <span>Contact: {addr.contact_person}</span>}
                      {addr.contact_phone && <span className="ml-2">Ph: {addr.contact_phone}</span>}
                    </p>
                  )}
                  {addr.notes && (
                    <p className="text-[10px] text-gray-400 italic truncate">Note: {addr.notes}</p>
                  )}
                </div>

                <div className="flex items-center gap-1.5 mt-3 ml-9 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" variant="outline" onClick={() => openEdit(addr)}
                    data-testid={`addr-edit-${addr.id}`}
                    className="h-7 px-2.5 text-[10px] gap-1">
                    <Pencil className="w-3 h-3" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openDelete(addr)}
                    data-testid={`addr-delete-${addr.id}`}
                    className="h-7 px-2.5 text-[10px] gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                    <Trash2 className="w-3 h-3" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="address-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-4 h-4" style={{ color: '#24549c' }} />
              {editingId ? 'Edit Address' : 'Add New Address'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Address Type <span className="text-red-500">*</span></label>
                <Select value={form.address_type} onValueChange={v => setForm(f => ({ ...f, address_type: v }))}>
                  <SelectTrigger data-testid="addr-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ship_to">Ship To (Delivery Address)</SelectItem>
                    <SelectItem value="consignee">Consignee (Receiving Authority)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Label / Nickname</label>
                <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. PWD Ranchi, NH Patna" data-testid="addr-label" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Company / Name <span className="text-red-500">*</span></label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Company or person name" data-testid="addr-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Full Address <span className="text-red-500">*</span></label>
              <Textarea rows={2} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="Street, locality..." data-testid="addr-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">City</label>
                <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="Ranchi" data-testid="addr-city" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">State</label>
                <Select value={form.state} onValueChange={v => setForm(f => ({ ...f, state: v }))}>
                  <SelectTrigger data-testid="addr-state"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Pincode</label>
                <Input value={form.pin} onChange={e => setForm(f => ({ ...f, pin: e.target.value }))}
                  placeholder="834001" data-testid="addr-pin" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Contact Person</label>
                <Input value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))}
                  placeholder="Name" data-testid="addr-contact-person" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Phone</label>
                <Input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                  placeholder="9876543210" data-testid="addr-contact-phone" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">GSTIN</label>
              <Input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))}
                placeholder="22AAAAA0000A1Z5" className="font-mono" data-testid="addr-gstin" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any additional notes..." data-testid="addr-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="addr-submit"
              className="bg-[#24549c] hover:bg-[#1a3f7a] gap-1.5">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : editingId ? <Pencil className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {editingId ? 'Update' : 'Save Address'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="max-w-sm" data-testid="delete-address-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-4 h-4" /> Delete Address
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <span className="font-semibold">{deleteTarget?.name}</span>?
            This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitting} data-testid="confirm-delete-addr"
              className="gap-1.5">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedAddressBook;
